import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { orderTemplateFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { OrderTemplateForm } from "@/forms/order-template-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditOrderTemplateModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	orderTemplateId: string;
}

export function EditOrderTemplateModal({
	onClose,
	isOpen,
	onComplete,
	orderTemplateId,
}: EditOrderTemplateModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["order-templates"][":orderTemplateId"].$get({
			param: { orderTemplateId },
		});
		const data = await responseHandler(response);
		return orderTemplateFormSchema.parse(data.orderTemplate);
	});
	const methods = useForm({
		resolver: resolver(orderTemplateFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit() {
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_order_template")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				width="95vw"
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<OrderTemplateForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
